import { CountryCode, CurrencyCode } from '@dayetopia/library';
import { AppState, CURRENCY_MIGRATIONS } from '@constants';
import { CurrencyObj } from '@contracts';
import { logger } from './logger';

/**
 * Checks if a given string is a valid CurrencyCode.
 *
 * @param {any} value - The value to check.
 * @returns {boolean} True if the value is a valid CurrencyCode, false otherwise.
 */
export function isValidCurrencyCode(value: any): value is CurrencyCode {
  return Object.values(CurrencyCode).includes(value);
}

/**
 * Gets the currency code for a given country code.
 *
 * @param {CountryCode} countryCode - The country code to get the currency code for.
 * @returns {CurrencyCode} The currency code for the given country code.
 */
export function getCurrencyFromCountryCode(countryCode: CountryCode): CurrencyCode {
  switch (countryCode) {
    case CountryCode.GB:
      return CurrencyCode.GBP;
    case CountryCode.US:
      return CurrencyCode.USD;
    case CountryCode.BG:
      return CurrencyCode.EUR;
    default:
      return CurrencyCode.EUR;
  }
}

/**
 * Gets the currency object for a given currency code.
 *
 * @param {CurrencyCode} currencyCode - The currency code to get the currency object for.
 * @returns {CurrencyObj} The currency object for the given currency code.
 */
export function getCurrencyFromCurrencyCode(currencyCode: CurrencyCode): CurrencyObj {
  switch (currencyCode) {
    case CurrencyCode.GBP:
      return {
        letterCode: CurrencyCode.GBP,
        symbol: '£'
      };
    case CurrencyCode.USD:
      return {
        letterCode: CurrencyCode.USD,
        symbol: '$'
      };
    case CurrencyCode.BGN:
      return {
        letterCode: CurrencyCode.BGN,
        symbol: 'лв'
      };
    default:
      return {
        letterCode: CurrencyCode.EUR,
        symbol: '€'
      };
  }
}

/**
 * Migrates deprecated currencies to their replacements and clears cart if migrated.
 *
 * @param {AppState} state - The state object containing currency and cart data.
 */
export function migrateDeprecatedCurrency(state: AppState): void {
  if (!state.currency) return;

  const migration = CURRENCY_MIGRATIONS.find((m) => m.old === state.currency.letterCode);

  if (migration) {
    /* eslint-disable no-param-reassign */
    state.currency = migration.new;
    state.cartItems = [];
    state.price = 0;
    /* eslint-enable no-param-reassign */
    logger.info('Cart cleared due to currency migration');
  }
}
